// Espelho de lib/phenology.js (o servidor é a autoridade; aqui é só pra montar a
// legenda OFFLINE no momento da captura). Curva Fehr & Caviness (soja). Ao mudar
// a curva no servidor, atualize este arquivo.

const REF_CICLO = 120;
const REF: Record<string, number> = {
  VC: 5, V1: 10, R1: 40, R2: 47, R3: 54, R4: 62, R5: 70, R6: 90, R7: 110, R8: 120,
};
const REF_CADENCIA_V = 4;
const EMERGENCE_OFFSET_DAYS = 6;
const MS_DIA = 86400000;

function ymd(v?: string | Date | null): number | null {
  if (!v) return null;
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? null : Date.UTC(v.getUTCFullYear(), v.getUTCMonth(), v.getUTCDate());
  }
  const s = String(v).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function diffDias(de?: string | Date | null, ate?: string | Date | null): number | null {
  const a = ymd(de);
  const b = ymd(ate);
  if (a == null || b == null) return null;
  return Math.floor((b - a) / MS_DIA);
}

function fmtBR(v?: string | Date | null): string | null {
  const t = ymd(v);
  if (t == null) return null;
  const d = new Date(t);
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
}

export function resolverEmergencia(o: { dataEmergencia?: string | null; dataPlantio?: string | null }): string | null {
  if (o.dataEmergencia) return o.dataEmergencia;
  const t = ymd(o.dataPlantio);
  if (t == null) return null;
  return new Date(t + EMERGENCE_OFFSET_DAYS * MS_DIA).toISOString().slice(0, 10);
}

export type Fase = 'emergencia' | 'vegetativo' | 'reprodutivo' | 'maturacao' | null;
export interface EstadioInfo {
  dae: number | null;
  estadio: string | null;
  fase: Fase;
  legendaMetric: string | null;
}

export function estimarEstadio(o: {
  dataEmergencia?: string | null;
  dataPlantio?: string | null;
  dataVisita?: string | Date | null;
  cicloDias?: number | null;
}): EstadioInfo {
  const emergencia = resolverEmergencia(o);
  const vazio: EstadioInfo = { dae: null, estadio: null, fase: null, legendaMetric: null };
  if (!emergencia || !o.dataVisita) return vazio;
  const dae = diffDias(emergencia, o.dataVisita);
  if (dae == null || dae < 0) return { ...vazio, dae: dae == null ? null : dae };

  const fator = (o.cicloDias && o.cicloDias > 0 ? o.cicloDias : REF_CICLO) / REF_CICLO;
  const s = (k: string) => Math.round((REF[k] ?? 0) * fator);
  const cadV = REF_CADENCIA_V * fator;

  let estadio: string;
  let fase: Fase;
  if (dae < s('VC')) {
    estadio = 'VE'; fase = 'emergencia';
  } else if (dae < s('V1')) {
    estadio = 'VC'; fase = 'emergencia';
  } else if (dae < s('R1')) {
    estadio = 'V' + (1 + Math.floor((dae - s('V1')) / cadV)); fase = 'vegetativo';
  } else if (dae < s('R8')) {
    const marcos = ['R1', 'R2', 'R3', 'R4', 'R5', 'R6', 'R7'];
    let atual = 'R1';
    for (const r of marcos) if (dae >= s(r)) atual = r;
    if (atual === 'R5') {
      const span = Math.max(1, s('R6') - s('R5'));
      const sub = Math.min(5, Math.max(1, 1 + Math.floor(((dae - s('R5')) * 5) / span)));
      estadio = 'R5.' + sub;
    } else {
      estadio = atual;
    }
    fase = atual === 'R7' ? 'maturacao' : 'reprodutivo';
  } else {
    estadio = 'R8'; fase = 'maturacao';
  }

  const legendaMetric = fase === 'emergencia' ? `DAE: ${dae} dias` : `Estádio ${estadio}`;
  return { dae, estadio, fase, legendaMetric };
}

export function montarLegenda(o: {
  numero?: string | null;
  variedade?: string | null;
  dataPlantio?: string | null;
  estadioInfo?: EstadioInfo | null;
  override?: { legendaMetric: string } | null;
}): string[] {
  const linhas: string[] = [];
  if (o.numero != null && o.numero !== '') linhas.push(`Talhão ${o.numero}`);
  if (o.variedade) linhas.push(`Cultivar: ${o.variedade}`);
  if (o.dataPlantio) {
    const p = fmtBR(o.dataPlantio);
    if (p) linhas.push(`Plantio: ${p}`);
  }
  const metrica = o.override?.legendaMetric || o.estadioInfo?.legendaMetric || null;
  if (metrica) linhas.push(metrica);
  return linhas;
}
